import React, { useState } from 'react';
import { Award, Sparkles, ShoppingBag, Heart, ExternalLink, Star } from 'lucide-react';
import '../styles/components/Shop.css';

const Shop = ({
   tryOnItems = [],
   user = {},
   userProfile = {},
   interests = [],
   searchQuery = '',
   setSearchQuery = () => { },
   handleTryOn = () => { }
}) => {
   const [shopTab, setShopTab] = useState('featured');

   // Default user data
   const currentUser = {
      name: user.name || userProfile.name || 'Alex Chen',
      username: user.username || userProfile.username || '@alexstyle',
      points: user.points || userProfile.points || 2840,
      level: user.level || 'Gold'
   };

   // Partner brands data
   const partnerBrands = [
      { name: 'Zara', discount: 25, exclusiveItems: 15, tier: 'Gold Partner' },
      { name: 'H&M', discount: 20, exclusiveItems: 12, tier: 'Silver Partner' },
      { name: 'Nike', discount: 30, exclusiveItems: 8, tier: 'Platinum Partner' },
      { name: 'Adidas', discount: 25, exclusiveItems: 10, tier: 'Gold Partner' },
      { name: 'Uniqlo', discount: 15, exclusiveItems: 6, tier: 'Bronze Partner' },
      { name: 'COS', discount: 20, exclusiveItems: 9, tier: 'Silver Partner' }
   ];

   // Featured items data
   const featuredItems = [
      {
         id: 1,
         brand: 'Nike',
         item: 'Air Force 1 x ILYO Edition',
         originalPrice: 120,
         discountedPrice: 84,
         pointsDiscount: 1000,
         exclusive: true,
         image: '/api/placeholder/150/200',
         description: 'Limited edition collaboration with ILYO users',
         rating: 4.8
      },
      {
         id: 2,
         brand: 'Zara',
         item: 'Minimalist Blazer',
         originalPrice: 89,
         discountedPrice: 67,
         pointsDiscount: 500,
         exclusive: false,
         image: '/api/placeholder/150/200',
         rating: 4.5
      },
      {
         id: 3,
         brand: 'H&M',
         item: 'Sustainable Cotton Tee',
         originalPrice: 12,
         discountedPrice: 10,
         pointsDiscount: 200,
         exclusive: true,
         image: '/api/placeholder/150/200',
         description: 'Eco-friendly exclusive for ILYO community',
         rating: 4.6
      },
      {
         id: 4,
         brand: 'Adidas',
         item: 'Ultraboost 22',
         originalPrice: 180,
         discountedPrice: 126,
         pointsDiscount: 750,
         exclusive: false,
         image: '/api/placeholder/150/200',
         rating: 4.9
      }
   ];

   // Points rewards
   const pointsRewards = [
      { points: 500, reward: 'Extra 10% off any purchase', icon: '💰' },
      { points: 1000, reward: 'Free shipping on next order', icon: '🚚' },
      { points: 1500, reward: '$20 ILYO credit', icon: '💳' },
      { points: 2000, reward: 'VIP early access to sales', icon: '⭐' },
      { points: 5000, reward: 'Personal stylist session', icon: '👗' }
   ];

   // Charity partners
   const charityPartners = [
      { name: 'Dress for Success', focus: 'Professional attire for women', impact: '2,847 outfits donated' },
      { name: 'Goodwill', focus: 'Job training & community support', impact: '15,392 items donated' },
      { name: 'The Salvation Army', focus: 'Family assistance programs', impact: '8,561 items donated' },
      { name: 'Local Homeless Shelter', focus: 'Warm clothing for winter', impact: '1,298 items donated' }
   ];

   // Sellable items
   const sellableItems = [
      { item: 'White Button Down', brand: 'Uniqlo', condition: 'Good', estimatedPoints: 45, worn: '3 days ago' },
      { item: 'Black Turtleneck', brand: 'COS', condition: 'Excellent', estimatedPoints: 85, worn: '1 week ago' },
      { item: 'Navy Blazer', brand: 'Zara', condition: 'Like New', estimatedPoints: 120, worn: '5 days ago' },
      { item: 'Running Shoes', brand: 'Nike', condition: 'Fair', estimatedPoints: 35, worn: '3 days ago' },
      { item: 'Leather Belt', brand: 'Coach', condition: 'Good', estimatedPoints: 95, worn: '3 days ago' }
   ];

   // Featured Items Screen
   const FeaturedScreen = () => (
      <div className="shop-screen">
         {/* Points Header */}
         <div className="points-header">
            <div className="points-card">
               <div className="points-amount">{currentUser.points}</div>
               <div className="points-label">points</div>
            </div>
            <div className="member-badge">{currentUser.level} Member</div>
         </div>

         {/* Exclusive Badge Unlocks */}
         <div className="exclusive-section">
            <div className="section-header">
               <Award className="section-icon" />
               <h3 className="section-title">Exclusive: Badge Unlocked Items</h3>
            </div>
            <p className="section-description">
               Special unreleased items only available to skilled stylists
            </p>

            <div className="exclusive-grid">
               <div className="exclusive-item">
                  <div className="exclusive-image">
                     <ShoppingBag size={32} className="placeholder-icon" />
                  </div>
                  <div className="exclusive-info">
                     <h4 className="exclusive-name">Limited Edition Blazer</h4>
                     <p className="exclusive-price">Exclusive Preview • $280</p>
                     <div className="badge-requirement">
                        🏆 Week Warrior Required
                     </div>
                     <button className="exclusive-button">Early Access</button>
                  </div>
               </div>

               <div className="exclusive-item">
                  <div className="exclusive-image">
                     <ShoppingBag size={32} className="placeholder-icon" />
                  </div>
                  <div className="exclusive-info">
                     <h4 className="exclusive-name">Designer Heels</h4>
                     <p className="exclusive-price">Coming Soon • $420</p>
                     <div className="badge-requirement">
                        💚 Charity Champion Required
                     </div>
                     <button className="exclusive-button">Reserve Now</button>
                  </div>
               </div>
            </div>

            <div className="exclusive-note">
               Unlock more exclusive items by earning achievement badges!
            </div>
         </div>

         {/* Featured Items */}
         <div className="featured-section">
            <h3 className="section-title">Featured Items</h3>

            <div className="items-grid">
               {featuredItems.map((item) => (
                  <div key={item.id} className="item-card">
                     {item.exclusive && (
                        <div className="exclusive-badge">
                           <Sparkles size={12} />
                           ILYO EXCLUSIVE
                        </div>
                     )}

                     <div className="item-image">
                        <ShoppingBag size={32} className="placeholder-icon" />
                     </div>

                     <div className="item-info">
                        <div className="item-header">
                           <h4 className="item-brand">{item.brand}</h4>
                           <div className="item-rating">
                              <Star size={12} className="star-filled" />
                              <span className="rating-value">{item.rating}</span>
                           </div>
                        </div>

                        <h5 className="item-name">{item.item}</h5>

                        {item.description && (
                           <p className="item-description">{item.description}</p>
                        )}

                        <div className="price-container">
                           <span className="current-price">${item.discountedPrice}</span>
                           <span className="original-price">${item.originalPrice}</span>
                           <span className="discount-badge">
                              {Math.round((1 - item.discountedPrice / item.originalPrice) * 100)}% OFF
                           </span>
                        </div>

                        <div className="points-discount">
                           💎 Use {item.pointsDiscount} pts for extra 10% off
                        </div>

                        <div className="item-actions">
                           <button className="add-to-cart-btn">Add to Cart</button>
                           <button className="wishlist-btn">
                              <Heart size={16} />
                           </button>
                        </div>
                     </div>
                  </div>
               ))}
            </div>
         </div>

         {/* Your Interests */}
         <div className="interests-section">
            <div className="section-header">
               <h3 className="section-title">Your Interests</h3>
               <button className="view-all-btn">View All</button>
            </div>

            <div className="interests-list">
               {interests.slice(0, 2).map((item, idx) => (
                  <div key={idx} className="interest-item">
                     <div className="interest-image">
                        <ShoppingBag size={24} className="placeholder-icon" />
                     </div>
                     <div className="interest-info">
                        <h4 className="interest-name">{item.item}</h4>
                        <p className="interest-brand">{item.brand} • {item.price}</p>
                        <p className="interest-discount">20% off with points!</p>
                     </div>
                     <div className="interest-actions">
                        <button className="buy-now-btn">Buy Now</button>
                        <button className="remove-btn">Remove</button>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
   );

   // Brands Screen
   const BrandsScreen = () => (
      <div className="shop-screen">
         <div className="brands-header">
            <h2 className="screen-title">Brand Partners</h2>
            <p className="screen-description">Exclusive deals from our fashion partners</p>
         </div>

         <div className="brands-grid">
            {partnerBrands.map((brand, idx) => (
               <div key={idx} className="brand-card">
                  <div className="brand-header">
                     <h3 className="brand-name">{brand.name}</h3>
                     <span className={`tier-badge ${brand.tier.replace(' ', '-').toLowerCase()}`}>
                        {brand.tier}
                     </span>
                  </div>

                  <div className="brand-stats">
                     <div className="brand-stat">
                        <p className="stat-label">Your Discount</p>
                        <p className="stat-value discount">{brand.discount}% OFF</p>
                     </div>
                     <div className="brand-stat">
                        <p className="stat-label">Exclusive Items</p>
                        <p className="stat-value">{brand.exclusiveItems} items</p>
                     </div>
                  </div>

                  <button className="shop-brand-btn">Shop {brand.name}</button>
               </div>
            ))}
         </div>
      </div>
   );

   // Points Screen
   const PointsScreen = () => (
      <div className="shop-screen">
         <div className="points-overview">
            <div className="points-display">
               <div className="points-amount large">{currentUser.points}</div>
               <div className="points-subtitle">Available Points</div>
            </div>
         </div>

         <div className="rewards-section">
            <h3 className="section-title">Redeem Points</h3>
            <div className="rewards-list">
               {pointsRewards.map((reward, idx) => (
                  <div key={idx} className="reward-item">
                     <div className="reward-info">
                        <span className="reward-icon">{reward.icon}</span>
                        <div className="reward-details">
                           <h4 className="reward-name">{reward.reward}</h4>
                           <p className="reward-cost">{reward.points} points required</p>
                        </div>
                     </div>
                     <button
                        className={`redeem-btn ${currentUser.points >= reward.points ? 'enabled' : 'disabled'
                           }`}
                        disabled={currentUser.points < reward.points}
                     >
                        {currentUser.points >= reward.points ? 'Redeem' : 'Locked'}
                     </button>
                  </div>
               ))}
            </div>
         </div>

         <div className="earn-points-section">
            <h3 className="section-title">How to Earn Points</h3>
            <div className="earning-methods">
               <div className="method-item">• Daily post: 10 points</div>
               <div className="method-item">• Weekly streak bonus: 50 points</div>
               <div className="method-item">• Friend likes your post: 2 points</div>
               <div className="method-item">• Purchase through app: 100 points</div>
               <div className="method-item">• Refer a friend: 200 points</div>
               <div className="method-item">• Sell clothes for charity: 25-150 points</div>
               <div className="method-item">• Complete challenges: 25-500 points</div>
            </div>
         </div>
      </div>
   );

   // Sell Screen
   const SellScreen = () => (
      <div className="shop-screen">
         <div className="sell-header">
            <h2 className="screen-title">Sell for Charity</h2>
            <div className="impact-card">
               <div className="impact-stats">
                  <div className="impact-label">Your Impact This Year</div>
                  <div className="impact-number">47 items donated</div>
                  <div className="impact-points">Earned 2,180 points helping others</div>
               </div>
            </div>
         </div>

         <div className="how-it-works">
            <h3 className="section-title">💝 How It Works</h3>
            <div className="steps-list">
               <div className="step-item">1. Select items from your closet to sell</div>
               <div className="step-item">2. We evaluate condition & brand value</div>
               <div className="step-item">3. You earn points, we donate clothes to charity</div>
               <div className="step-item">4. Track your impact & help those in need</div>
            </div>
         </div>

         <div className="sellable-items-section">
            <h3 className="section-title">Items You Can Sell</h3>
            <div className="sellable-items">
               {sellableItems.map((item, idx) => (
                  <div key={idx} className="sellable-item">
                     <div className="sellable-info">
                        <h4 className="sellable-name">{item.item}</h4>
                        <p className="sellable-details">{item.brand} • {item.condition} condition</p>
                     </div>
                     <div className="sellable-points">
                        <div className="points-estimate">+{item.estimatedPoints} pts</div>
                        <div className="points-label">estimated</div>
                     </div>
                     <button className="sell-btn">Sell for Charity</button>
                  </div>
               ))}
            </div>
         </div>

         <div className="charity-partners-section">
            <h3 className="section-title">Our Charity Partners</h3>
            <div className="partners-list">
               {charityPartners.map((charity, idx) => (
                  <div key={idx} className="charity-partner">
                     <div className="charity-info">
                        <h4 className="charity-name">{charity.name}</h4>
                        <p className="charity-focus">{charity.focus}</p>
                     </div>
                     <p className="charity-impact">{charity.impact}</p>
                  </div>
               ))}
            </div>
         </div>

         <div className="charity-recognition">
            <h3 className="section-title">🌟 Special Recognition</h3>
            <p className="recognition-text">
               Sell 10+ items to unlock the "Charity Champion" badge and earn 500 bonus points!
            </p>
            <div className="progress-info">
               Progress: 7/10 items (3 more to go!)
            </div>
         </div>
      </div>
   );

   return (
      <div className="shop-container">
         {/* Shop Header */}
         <div className="shop-header">
            <h1 className="shop-title">ilyo shop</h1>
            <div className="shop-search">
               <input
                  type="text"
                  placeholder="Search brands, items, styles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
               />
            </div>
         </div>

         {/* Search Results */}
         {searchQuery && (
            <div className="search-results">
               <div className="results-header">Shop Results:</div>
               <div className="search-items">
                  <div className="search-item">
                     <div className="search-image">
                        <ShoppingBag size={20} className="placeholder-icon" />
                     </div>
                     <div className="search-info">
                        <h4 className="search-name">Oversized Cotton Tee</h4>
                        <p className="search-details">Uniqlo • $19.90</p>
                     </div>
                     <button className="add-btn">Add</button>
                  </div>
                  <div className="search-item">
                     <div className="search-image">
                        <ShoppingBag size={20} className="placeholder-icon" />
                     </div>
                     <div className="search-info">
                        <h4 className="search-name">Leather Jacket</h4>
                        <p className="search-details">Zara • $129.99</p>
                     </div>
                     <button className="add-btn">Add</button>
                  </div>
               </div>
            </div>
         )}

         {/* Navigation Tabs */}
         <div className="shop-tabs">
            {[
               { id: 'featured', label: 'Featured' },
               { id: 'brands', label: 'Brands' },
               { id: 'points', label: 'Points' },
               { id: 'sell', label: 'Sell for Charity' }
            ].map(({ id, label }) => (
               <button
                  key={id}
                  onClick={() => setShopTab(id)}
                  className={`shop-tab ${shopTab === id ? 'active' : ''}`}
               >
                  {label}
               </button>
            ))}
         </div>

         {/* Tab Content */}
         <div className="shop-content">
            {shopTab === 'featured' && <FeaturedScreen />}
            {shopTab === 'brands' && <BrandsScreen />}
            {shopTab === 'points' && <PointsScreen />}
            {shopTab === 'sell' && <SellScreen />}
         </div>
      </div>
   );
};

export default Shop;